import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { ArrowLeft, Image, ExternalLink, Clock, AlertCircle } from "lucide-react";
import { Id } from "../../../convex/_generated/dataModel";

function isValidConvexId(id: string): boolean {
  // Convex IDs are typically alphanumeric, lowercase, 24+ chars
  return /^[a-z0-9]{20,}$/.test(id);
}

export default function Gallery() {
  const { guestId } = useParams<{ guestId: string }>();
  const navigate = useNavigate();

  // Early return for invalid ID format
  if (guestId && !isValidConvexId(guestId)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-2 text-slate-500 p-4">
        <AlertCircle className="w-10 h-10 text-slate-300" />
        <p className="font-bold text-slate-800">Tamu Tidak Ditemukan</p>
        <p className="text-xs text-slate-500">ID tamu tidak valid.</p>
        <Button size="sm" onClick={() => navigate("/")}>Kembali ke Beranda</Button>
      </div>
    );
  }

  const guest = useQuery(
    api.guests.getById,
    guestId ? { id: guestId as Id<"guests"> } : "skip"
  );

  const matches = useQuery(
    api.faceMatcher.listMatchesByGuest,
    guestId ? { guestId: guestId as Id<"guests"> } : "skip"
  );

  if (guest === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
        Memuat galeri...
      </div>
    );
  }

  if (guest === null) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-2 text-slate-500">
        <p className="font-bold text-slate-800">Tamu Tidak Ditemukan</p>
        <Button size="sm" onClick={() => navigate("/")}>Kembali ke Beranda</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="px-6 py-4 flex items-center justify-between border-b bg-white">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <span className="font-bold text-base text-slate-900">Galeri Foto Saya</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6 w-full">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Halo, {guest.name} 👋</CardTitle>
            <p className="text-xs text-slate-500">
              Foto-foto di bawah ini cocok dengan wajahmu. Klik untuk buka ukuran penuh.
            </p>
          </CardHeader>
        </Card>

        {matches === undefined ? (
          <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
            Memuat foto yang cocok...
          </div>
        ) : matches.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent className="flex flex-col items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                <Image className="w-7 h-7" />
              </div>
              <p className="font-semibold text-slate-700">Belum Ada Foto yang Cocok</p>
              <p className="text-xs text-slate-500 max-w-sm">
                Fotografer belum mengunggah album acara atau wajahmu belum terdeteksi. Cek lagi nanti ya!
              </p>
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <Clock className="w-3.5 h-3.5" />
                <span>Otomatis ter-update ketika foto baru tersedia</span>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {matches.map((m: any) => (
              <a
                key={m._id}
                href={m.photoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-xl overflow-hidden border bg-white hover:shadow-md transition block"
              >
                <div className="aspect-square bg-slate-100 overflow-hidden">
                  <img
                    src={m.photoUrl}
                    alt={m.photoName || "Foto event"}
                    className="w-full h-full object-cover group-hover:scale-105 transition"
                    loading="lazy"
                  />
                </div>
                <div className="p-2 flex items-center justify-between">
                  <span className="text-xs text-slate-600 truncate">{m.photoName || "Foto"}</span>
                  <ExternalLink className="w-3 h-3 text-slate-400 shrink-0" />
                </div>
              </a>
            ))}
          </div>
        )}

        <div className="text-center pt-4">
          <p className="text-xs text-slate-400">
            Skor kecocokan minimal 0.6. Foto ditandai <span className="font-semibold">uncertain</span> jika skor 0.6–0.75.
          </p>
        </div>
      </main>
    </div>
  );
}